import { z } from 'zod';
import { KNOWN_SCRATCHPAD_PATHS } from '../scratchpad/paths.js';
import { snakeKey } from './keys.js';
/**
 * Form Node: a block of structured questions the agent asks. The author
 * writes items as free prose; an inference pass fills in the structured
 * fields (id, answerType, required, template variables, scratchpad mapping).
 * During a call each question raises `question_answered` or
 * `question_skipped`, and the walk ends with `question_block_completed`.
 * Edges read them through equation rows.
 */
/* ---------- answer types ---------- */
export const answerTypeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('yes_no') }),
    z.object({
        kind: z.literal('options'),
        options: z.array(z.string().min(1).max(64)).min(2).max(16),
    }),
    z.object({
        kind: z.literal('integer'),
        min: z.number().int().optional(),
        max: z.number().int().optional(),
    }),
    z.object({ kind: z.literal('string') }),
    z.object({ kind: z.literal('zip') }),
    z.object({ kind: z.literal('email') }),
    z.object({ kind: z.literal('date') }),
    z.object({ kind: z.literal('free_text') }),
]);
/* ---------- scratchpad binding ---------- */
/**
 * Two-way binding to a scratchpad path. The render-mode decision happens
 * ONCE per question, at the moment the executor reaches it in the item
 * walk — it is a snapshot of what the scratchpad holds at that instant.
 * A scratchpad value that lands mid-form (e.g. a tool write between items)
 * does NOT re-decide a question whose decision was already taken; nor does
 * a question's decision react to writes landing on later items.
 *
 * Modes picked at the question's turn:
 *   - skip:    scratchpad has a confident value — never asked, answer is
 *              pre-populated in the outcome event.
 *   - confirm: scratchpad has a less-confident value — asked as a yes/no
 *              confirmation ("is {value} right?").
 *   - capture: scratchpad is empty — asked as an open question.
 *
 * `renderPolicy` lets the author override the runtime's confidence-based
 * decision per question:
 *   - auto:                runtime picks skip | confirm | capture from
 *                          scratchpad confidence (webhook = high,
 *                          LLM-extracted = medium, empty = none).
 *   - always_capture:      ignore scratchpad, always ask fresh.
 *   - confirm_if_present:  if any value exists, ask "I have X — right?"
 *                          Otherwise capture.
 *   - skip_if_present:     if any value exists, trust it and skip.
 */
export const SCRATCHPAD_DIRECTIONS = ['read_write', 'write_only'];
export const SCRATCHPAD_RENDER_POLICIES = [
    'auto',
    'always_capture',
    'confirm_if_present',
    'skip_if_present',
];
const SCRATCHPAD_PATH_VALUES = KNOWN_SCRATCHPAD_PATHS.map(p => p.path);
export const scratchpadMappingSchema = z.object({
    /** Must match one of KNOWN_SCRATCHPAD_PATHS so the runtime can fill it. */
    path: z
        .string()
        .min(1)
        .max(256)
        .refine(value => SCRATCHPAD_PATH_VALUES.includes(value), {
        message: `path must be one of the known scratchpad paths`,
    }),
    direction: z.enum(SCRATCHPAD_DIRECTIONS).default('read_write'),
    renderPolicy: z.enum(SCRATCHPAD_RENDER_POLICIES).default('auto'),
});
/* ---------- items ---------- */
export const FORM_ITEM_MODES = ['verbatim', 'instruction'];
const itemBase = {
    id: snakeKey,
    /** The author's free-prose line. The agent either speaks it verbatim or
     *  treats it as an instruction, depending on `mode`. */
    text: z.string().min(1).max(2000),
    /**
     * `verbatim`    — the agent speaks `text` literally via TTS.
     * `instruction` — the agent treats `text` as a direction and generates its
     *                 reply via the LLM in character. The generated reply (not
     *                 the instruction) is what the transcript records.
     */
    mode: z.enum(FORM_ITEM_MODES).default('verbatim'),
    /**
     * Nesting level. V1 caps at 1 — 0 is a top-level item, 1 is a sub-item
     * belonging to the nearest preceding depth-0 item (renders as 1a/1b/...).
     * V1 treats nesting as a semantic hint only; conditional branching based
     * on parent answer is a V2 concern (per the deferred design decision).
     */
    depth: z.union([z.literal(0), z.literal(1)]).default(0),
    /** Stable React key — survives inference-driven id renames. */
    renderKey: z.string().min(1).max(64).optional(),
};
export const formPromptItemSchema = z.object({
    kind: z.literal('prompt'),
    ...itemBase,
});
export const formQuestionItemSchema = z.object({
    kind: z.literal('question'),
    ...itemBase,
    answerType: answerTypeSchema,
    /**
     * Soft-required. The node always advances (soft-store policy). A required
     * question that was not answered surfaces as `answered: false` in the
     * outcome event so downstream can route on it.
     */
    required: z.boolean().default(false),
    /**
     * {token} placeholders in `text` mapped to scratchpad paths. The agent
     * substitutes these at render time (e.g. {zip} -> "30312"). Only populated
     * when the author's text used placeholders.
     */
    templateVars: z.record(z.string().min(1).max(256)).optional(),
    scratchpadMapping: scratchpadMappingSchema.optional(),
});
export const formItemSchema = z.discriminatedUnion('kind', [
    formPromptItemSchema,
    formQuestionItemSchema,
]);
/* ---------- signals ---------- */
/**
 * A signal is a true or false question about the conversation. A conversation
 * node lists the things it listens for; nobody speaks them. The runtime judges
 * each one over the conversation since the node was entered and, when it can
 * tell, raises `signal_resolved` with the value true or false. An edge reads
 * it as a boolean variable.
 */
export const signalSchema = z.object({
    id: snakeKey,
    /** What the node listens for, as a true or false question, in the author's words. */
    text: z.string().min(1).max(2000),
    /** Stable React key — survives inference-driven id renames. */
    renderKey: z.string().min(1).max(64).optional(),
});
/* ---------- node ---------- */
/**
 * The type-specific part of a Form Node, mirroring the conversation / say
 * configuration schemas in graph.ts. Stored as the node row's typed
 * `configuration` JSON column so the DAO can parse it back out. The node
 * schema itself, with its tools and knowledge, is `formNodeSchema` in graph.ts.
 */
export const formConfigurationSchema = z.object({
    items: z.array(formItemSchema).min(1).max(32),
});
/* ---------- event names the runtime emits ---------- */
/** Per-turn: a question item received a reply and was coerced. */
export const QUESTION_ANSWERED = 'question_answered';
/** Per-turn: a question item was skipped by the scratchpad render ladder. */
export const QUESTION_SKIPPED = 'question_skipped';
/** Block-level: all items processed and no earlier edge fired. */
export const QUESTION_BLOCK_COMPLETED = 'question_block_completed';
/** Per-turn: a conversation node's signal was judged to hold. */
export const SIGNAL_RESOLVED = 'signal_resolved';
/**
 * Per scored turn: how the form read the caller's turn and what it did
 * (payload: itemId, label, intent, action, topScore, margin, scores). Not
 * routable; it is there to be observed.
 */
export const FORM_TURN_CLASSIFIED = 'form_turn_classified';
/* ---------- outcome shape ---------- */
export const CONFORMANCE_VALUES = [
    'conforms',
    'unclear',
    'off_type',
    'not_applicable',
];
export const RENDER_MODE_VALUES = ['skip', 'confirm', 'capture'];
//# sourceMappingURL=form-node.js.map